import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const title = 'select - Provided Fields';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  title,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "select"
    }}>{`Select`}</h1>
    <p>{`Select one value from list of options, where options can be a list of value, label to value map, or list of any object`}</p>
    <h2 {...{
      "id": "schema"
    }}>{`Schema`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-yml"
      }}>{`<fieldName in value>:
  type: select
  label: string? # fieldName to display
  defaultVal: any? # default value
  helperText: string? # text to help user to input
  disabled: boolean? # disable the input, default false
  options: Options # options can be a list of value, label to value map, or list of any object
  # the path or key name to get option value inside an option object
  valueKey: DigKey?
  # the path or key name to get label to display inside an option object
  labelKey: DigKey?
`}</code></pre>
    <h2 {...{
      "id": "example---list-of-value"
    }}>{`Example - list of value`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`() => {
  const [value, setValue] = useState({})
  const fields = useMemo(() => jsyaml.load(\`
  myField:
    type: select
    label: my field
    helperText: help
    placeholder: Please select
    options:
      - value1
      - value2
      - value3
  \`), [])
  return (
    <div>
      <BaseForm fields={fields} value={value} onChange={setValue}/>
      value:
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </div>
  )
}
`}</code></pre>
    <h2 {...{
      "id": "example---label-to-value-map"
    }}>{`Example - label to value map`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`() => {
  const [value, setValue] = useState({})
  const fields = useMemo(() => jsyaml.load(\`
  myField:
    type: select
    label: my field
    helperText: help
    placeholder: Please select
    options:
      label1: value1
      label2: value2
      label3: value3
  \`), [])
  return (
    <div>
      <BaseForm fields={fields} value={value} onChange={setValue}/>
      value:
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </div>
  )
}
`}</code></pre>
    <h2 {...{
      "id": "example---list-of-object"
    }}>{`Example - list of object`}</h2>
    <p>{`If you did not specify `}<inlineCode parentName="p">{`valueKey`}</inlineCode>{` or `}<inlineCode parentName="p">{`labelKey`}</inlineCode>{`, reforml will first try to search for `}<inlineCode parentName="p">{`value`}</inlineCode>{` key or `}<inlineCode parentName="p">{`label`}</inlineCode>{` key in the option.
If reforml cannot find the specific value, it will just use the object as the value, while stringify the object as the label`}</p>
    <h3 {...{
      "id": "specify-a-value-and-a-label-in-each-option"
    }}>{`Specify a value and a label in each option`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`() => {
  const [value, setValue] = useState({})
  const fields = useMemo(() => jsyaml.load(\`
  myField:
    type: select
    label: my field
    helperText: help
    placeholder: Please select
    options:
      - value: value1
        label: label1
      - value: value2
        label: label2
      - value: value3
        label: label3
  \`), [])
  return (
    <div>
      <BaseForm fields={fields} value={value} onChange={setValue}/>
      value:
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </div>
  )
}
`}</code></pre>
    <h3 {...{
      "id": "specify-valuekey-and-labelkey-to-search-inside-each-option"
    }}>{`Specify valueKey and labelKey to search inside each option`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`() => {
  const [value, setValue] = useState({})
  const fields = useMemo(() => jsyaml.load(\`
  myField:
    type: select
    label: my field
    helperText: help
    placeholder: Please select
    options:
      - key1: value1
        key2:
          key3: label1
      - key1: value2
        key2:
          key3: label2
      - key1: value3
        key2:
          key3: label3
    valueKey: key1
    labelKey:
      - key2
      - key3
  \`), [])
  return (
    <div>
      <BaseForm fields={fields} value={value} onChange={setValue}/>
      value:
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </div>
  )
}
`}</code></pre>
    <h2 {...{
      "id": "use-your-own"
    }}>{`Use Your own`}</h2>
    <p>{`Use `}<inlineCode parentName="p">{`useProcessOptionsMemo<unknown>(options, { labelKey, valueKey })`}</inlineCode>{` hook to process the options into array of value label pairs, e.g.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// input
useProcessOptionsMemo({label1: 'value1', label2: 'value2'})
// will give
[{
  value: 'value1',
  label: 'label1'
},{
  value: 'value2',
  label: 'label2'
}]
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import {
  Fields,
  OptionsFieldComponent,
  FieldPropTypes,
  ReformlProvider,
  BaseForm,
  useProcessOptionsMemo
} from 'reforml'
import React, { FunctionComponent, useState } from 'react'
import PropTypes from 'prop-types'

const MySelect: OptionsFieldComponent<unknown> = ({
  helperText,
  onChange,
  value,
  label,
  defaultVal,
  options,
  labelKey,
  valueKey,
  ...props
}) => {
  const valueLabel = useProcessOptionsMemo<unknown>(options, { labelKey, valueKey })
  const handleChange = ({ target: { value } }: { target: { value: string } }): void => {
    onChange?.(valueLabel[Number.parseInt(value)]?.value)
  }
  return (
    <div>
      <div>{label}:
        <select className='form-control' onChange={handleChange} {...props}>
          <option value="" selected={value === undefined}>{props.placeholder}</option>
          {valueLabel.map(({ label }, index) => (
            <option key={label} value={index} selected={value === valueLabel[index].value}>{label}</option>
          ))}
        </select>
      </div>
      <div>{helperText}</div>
    </div>
  )
}

MySelect.propTypes = {
  ...FieldPropTypes,
  value: PropTypes.string
}

const TextPage: FunctionComponent = () => {
  const [value, setValue] = useState({})
  console.log(value)
  const fields: Fields = {
    myField: {
      type: 'select',
      options: [{
        key1: 'value1',
        key2: {
          key3: 'label1'
        }
      }]
    }
  }
  return (
    <ReformlProvider fieldComponents={{ select: MySelect }}>
      <BaseForm fields={fields} onChange={setValue} value={value}/>
    </ReformlProvider>
  )
}

export default TextPage
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      